import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe } from '../core/translate.pipe';
import { DsButtonDirective, DsCardDirective, DsCheckboxDirective, DsInputDirective } from '../ui';
import { LocalizationService } from '../core/localization.service';
import {
  PlatformApi,
  type CandidateInvitation,
  type CandidateProgress,
  type CurrentUser,
} from '../core/platform-api';

@Component({
  selector: 'app-candidate-page',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    DsButtonDirective,
    DsCardDirective,
    DsCheckboxDirective,
    DsInputDirective,
  ],
  templateUrl: './candidate-page.html',
  styleUrl: './candidate-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CandidatePage {
  private readonly api = inject(PlatformApi);
  private readonly route = inject(ActivatedRoute);
  private readonly i18n = inject(LocalizationService);

  protected readonly user = signal<CurrentUser | null>(null);
  protected readonly invitation = signal<CandidateInvitation | null>(null);
  protected readonly progress = signal<CandidateProgress | null>(null);
  protected readonly busy = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly email = new FormControl('demo.candidate@example.test', {
    nonNullable: true,
    validators: [Validators.required, Validators.email],
  });
  protected readonly token = new FormControl(this.route.snapshot.queryParamMap.get('token') ?? '', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(32)],
  });
  protected readonly consent = new FormControl(false, {
    nonNullable: true,
    validators: [Validators.requiredTrue],
  });

  protected async login(): Promise<void> {
    if (this.email.invalid) return;
    await this.run(async () => this.user.set(await this.api.developmentLogin(this.email.value)));
  }

  protected async redeem(): Promise<void> {
    if (this.token.invalid) return;
    await this.run(async () =>
      this.invitation.set(await this.api.redeemInvitation(this.token.value)),
    );
  }

  protected async acceptAndStart(): Promise<void> {
    const invitation = this.invitation();
    if (!invitation || this.consent.invalid) {
      this.consent.markAsTouched();
      return;
    }

    await this.run(async () => {
      await this.api.acceptCandidateConsent(invitation.id);
      this.progress.set(await this.api.startOrResumeAssessment(invitation.id));
    });
  }

  protected async answer(code: string): Promise<void> {
    const invitation = this.invitation();
    const item = this.progress()?.currentItem;
    if (!invitation || !item) return;
    await this.run(async () => {
      this.progress.set(await this.api.submitCandidateAnswer(invitation.id, item.itemId, code));
    });
  }

  private async run(action: () => Promise<void>): Promise<void> {
    this.busy.set(true);
    this.error.set(null);
    try {
      await action();
    } catch {
      this.error.set(this.i18n.t('candidate.error'));
    } finally {
      this.busy.set(false);
    }
  }
}
